import { useMemo, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppSession } from '@/contexts/SessionContext';
import { BackButton } from './BackButton';
import { SidebarRail, useSidebarRailActive } from './SidebarRail';
import { sessionsToRailItems } from './railItems';
import { ConditionalMobileDevToggle, ConditionalRailDevToggle } from '@/components/DevPanel';
import { PaperMobileTabBar } from './PaperMobileTabBar';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_TINY_CLASS,
  PAPER_ICON_STROKE_WIDTH,
} from './paperShapes';

export type PaperSettingsTab = 'general' | 'billing';

export interface PaperAccountInfo {
  initials: string;
  displayName: string;
  metaLine: string;
  onLogout?: () => void;
}

export interface PaperSettingsShellProps {
  activeTab: PaperSettingsTab;
  account: PaperAccountInfo;
  children: ReactNode;
}

const TABS: { id: PaperSettingsTab; label: string; to: string }[] = [
  { id: 'general', label: 'General', to: '/settings' },
  { id: 'billing', label: 'Billing', to: '/settings/billing' },
];

export const PaperSettingsShell = (props: PaperSettingsShellProps) => {
  const navigate = useNavigate();
  const railActive = useSidebarRailActive();
  const { session } = useAppSession();
  const activeChatId = session.chat.activeChatId;
  const railItems = useMemo(
    () => sessionsToRailItems(session.chatHistory.sessions, activeChatId ?? null),
    [session.chatHistory.sessions, activeChatId],
  );
  const { activeTab, account, children } = props;

  return (
    <div
      className="absolute inset-0 flex flex-col bg-[var(--bg)] text-[var(--fg)]"
      style={{ fontFamily: 'var(--font-sans-paper)' }}
    >
      <div className="flex-1 min-h-0 flex">
        <div className="hidden desktop:contents">
          <SidebarRail
            active={railActive}
            pinned={railItems.pinned}
            recents={railItems.recents}
            onNavigate={(id) => {
              if (id === 'chat') navigate('/');
              if (id === 'agents') navigate('/agents');
              if (id === 'settings') navigate('/settings');
            }}
            bottomSlot={(expanded) => <ConditionalRailDevToggle expanded={expanded} />}
            account={{
              initials: account.initials,
              displayName: account.displayName,
              metaLine: account.metaLine,
              onSettings: () => navigate('/settings'),
              onLogout: account.onLogout,
            }}
          />
        </div>

        <div className="flex-1 flex flex-col min-w-0 overflow-auto">
        {/* top bar with Back */}
        <div
          className={cn(
            'flex items-center justify-between',
            'border-b border-solid border-[var(--hair)]',
            'px-[16px] py-[12px] desktop:px-[28px] desktop:py-[14px]',
          )}
        >
          <BackButton />
        </div>

        <div className="px-[16px] pt-[20px] pb-[24px] desktop:px-[40px] desktop:pt-[32px] desktop:pb-[40px] w-full max-w-[760px] mx-auto">
          <div className="mb-[6px] text-[11px] tracking-[0.14em] uppercase font-normal text-[var(--fg-3)]">
            Workspace
          </div>
          <h1
            className="text-[36px] m-0 text-[var(--fg)] leading-[1.05]"
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.01em', fontWeight: 400 }}
          >
            Settings
          </h1>

          {/* horizontal tabs */}
          <div className="flex gap-[24px] mt-[22px] border-b border-solid border-[var(--hair)]">
            {TABS.map((t) => {
              const isActive = t.id === activeTab;
              return (
                <Link
                  key={t.id}
                  to={t.to}
                  className={cn(
                    'py-[10px] text-[13px] transition-colors duration-150',
                    '-mb-px border-b-[1.5px] border-solid',
                    isActive
                      ? 'text-[var(--fg)] font-medium border-[var(--periwinkle)]'
                      : 'text-[var(--fg-3)] font-normal border-transparent hover:text-[var(--fg)]',
                  )}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>

          {/* account card */}
          <div
            className={cn(
              'mt-[24px] p-[20px] flex items-center gap-[16px]',
              'bg-[var(--surface)] border border-solid border-[var(--hair)] rounded-lg',
            )}
          >
            <div
              className={cn(
                'w-[44px] h-[44px] rounded-full inline-flex items-center justify-center shrink-0',
                'bg-[var(--periwinkle-wash)] text-[var(--periwinkle)]',
                'border border-solid border-[var(--periwinkle-veil)]',
                'text-[13px] font-semibold tracking-[0.06em]',
              )}
            >
              {account.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-[var(--fg)] truncate">
                {account.displayName}
              </div>
              <div className="text-[12px] text-[var(--fg-3)] mt-[2px] truncate">
                {account.metaLine}
              </div>
            </div>
            {account.onLogout && (
              <button
                type="button"
                onClick={account.onLogout}
                className={cn(
                  'inline-flex items-center gap-[6px] h-[30px] px-[12px]',
                  PAPER_CONTROL_RADIUS_CLASS,
                  'text-[11px] font-medium tracking-[0.02em] cursor-pointer',
                  'bg-transparent text-[var(--fg)] border border-solid border-[var(--hair)]',
                  'transition-colors duration-200',
                  'hover:bg-[var(--periwinkle-wash)] hover:text-[var(--periwinkle)] hover:border-[var(--periwinkle)]',
                )}
              >
                <LogOut className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
                Log out
              </button>
            )}
          </div>

          {children}

          <div className="text-center mt-[36px] text-[12px] text-[var(--fg-4)]">
            Need help?{' '}
            <a
              href="mailto:support@symbolica.ai"
              className="text-[var(--fg-2)] border-b border-solid border-[var(--hair)] pb-px"
            >
              Send us an email
            </a>
          </div>
        </div>
        </div>
      </div>

      <PaperMobileTabBar
        className="desktop:hidden"
        active="settings"
        devControlSlot={<ConditionalMobileDevToggle />}
      />
    </div>
  );
};
